import { useEffect, useState } from 'react'
import type { ChatDeck, ChatImage } from './api'

/**
 * The chat composer's unsent entry — the text typed and the images and decks attached, not sent yet — kept
 * per bid so it outlives ChatPanel: App.tsx only mounts the panel on the editor and dashboard
 * views, so a trip to Home, Knowledge, Bid settings, Account settings or Releases unmounts it and
 * used to throw the half-written prompt away with it.
 *
 * Keyed by bid because the chat is: a draft typed in bid A must never show up in, or be sent to,
 * bid B. Opening another part of the same bid keeps it. An emptied entry is forgotten rather than
 * stored, so sending the message or clearing the field by hand leaves nothing to restore.
 *
 * In memory only, like chat-prompt-history.ts — module-level, lost on reload or app restart, never
 * written to localStorage/sessionStorage or sent to a server: a draft can hold confidential bid
 * content. Same module-level-store + subscribe shape as that file.
 */
export interface ChatDraft {
  text: string
  images: ChatImage[]
  /** The PowerPoint decks staged for the agent (lib/chat-decks.ts, AB#452); absent when none. */
  decks?: ChatDeck[]
}

const EMPTY: ChatDraft = { text: '', images: [] }

/** Replaced on every write, never mutated, so a component holding it in state re-renders. */
let drafts: ReadonlyMap<string, ChatDraft> = new Map()
const listeners = new Set<(d: ReadonlyMap<string, ChatDraft>) => void>()

/** "No bid selected" is a slot of its own, never shared with a real bid. */
const keyOf = (bid: string | null): string => bid ?? ''

function emit(): void {
  for (const l of listeners) l(drafts)
}

function write(bid: string | null, next: ChatDraft): void {
  const copy = new Map(drafts)
  if (next.text === '' && next.images.length === 0 && !next.decks?.length) copy.delete(keyOf(bid))
  else copy.set(keyOf(bid), next)
  drafts = copy
  emit()
}

/** Imperative snapshot of `bid`'s unsent entry (an empty one when there is none). */
export function getChatDraft(bid: string | null): ChatDraft {
  return drafts.get(keyOf(bid)) ?? EMPTY
}

/** Set the text of `bid`'s unsent entry; its images are left as they are. */
export function setChatDraftText(bid: string | null, text: string): void {
  write(bid, { ...getChatDraft(bid), text })
}

/** Set the images of `bid`'s unsent entry, as a list or as an update of the stored one. The update
 *  reads the store, not a component's closure, so an image still being read when the member moves
 *  on lands on the bid it was attached in. */
export function setChatDraftImages(
  bid: string | null,
  update: ChatImage[] | ((prev: ChatImage[]) => ChatImage[]),
): void {
  const prev = getChatDraft(bid)
  write(bid, { ...prev, images: typeof update === 'function' ? update(prev.images) : update })
}

/** Set the decks of `bid`'s unsent entry, as a list or as an update of the stored one — read from
 *  the store, like the images, so a deck still uploading when the member moves on lands on its bid. */
export function setChatDraftDecks(
  bid: string | null,
  update: ChatDeck[] | ((prev: ChatDeck[]) => ChatDeck[]),
): void {
  const { decks: prev = [], ...rest } = getChatDraft(bid)
  const decks = typeof update === 'function' ? update(prev) : update
  write(bid, decks.length ? { ...rest, decks } : rest)
}

/** Forget every entry. For tests: the store is module-level, so it outlives a single test. */
export function resetChatDrafts(): void {
  drafts = new Map()
  emit()
}

/** Reactive view of `bid`'s unsent entry. Follows `bid`: when it changes, the new bid's entry is
 *  returned from that very render, never the previous bid's. */
export function useChatDraft(bid: string | null): ChatDraft {
  const [state, setState] = useState(drafts)
  useEffect(() => {
    const l = (d: ReadonlyMap<string, ChatDraft>): void => setState(d)
    listeners.add(l)
    l(drafts)
    return () => {
      listeners.delete(l)
    }
  }, [])
  return state.get(keyOf(bid)) ?? EMPTY
}
